/**
 * 一般情况下，在rem布局中 rem 的相对大小只受到 html 标签的 fontSize 影响
 * 但是在微信中，设置字体的大小也会影响到 rem 的比例，
 * 所以在微信应用页面中通过rem 适配的页面就会发生一些不可预料的错乱。
 * 为了解决上述问题就需要在动态调取 calc 函数时获取到微信调整字体的比例的倒数 scale，
 * 然后给 rem 的比例还原，为了使 rem 的值恢复正常就得和 scale 想乘。
 */
export default class ScaleHtmlFontSize {

    // 偏差值: html.fontsize 与 1rem width的div 大小的误差
    private offset: number;
    // 设计稿宽度
    private designWidth!: number;
    // html 元素
    private html: HTMLElement | null = null;
    // 参照系
    private referDiv: HTMLDivElement;
    // 变换系数
    private scale: number = 1;
    // 重复调用计算timer
    private reScaleTimer: any = 0;

    constructor(opt?: any) {
        this.designWidth = opt?.designWidth || 750;
        this.offset = opt?.offset || 0.1;

        const html = document.documentElement;
        const resizeEvent = 'orientationchange' in window ? 'orientationchange' : 'resize';
        this.html = html;
        this.referDiv = this.createReferDom();
        this.toScale();
        this.calc();
        if (document.addEventListener) {
            window.addEventListener(resizeEvent, this.calc.bind(this), false);
            window.addEventListener('load', this.calc.bind(this), false);
            document.addEventListener('DOMContentLoaded', this.calc.bind(this), false);
            // 微信内置浏览器打开页面后调整字体大小
            if (typeof WeixinJSBridge === 'undefined') {
                if (document.addEventListener) {
                    document.addEventListener(
                        'WeixinJSBridgeReady',
                        this.onWeixinJSBridgeReady.bind(this),
                        false);
                } else if ((document as any).attachEvent) {
                    (document as any)
                        .attachEvent('WeixinJSBridgeReady', this.onWeixinJSBridgeReady)
                        .bind(this);
                    (document as any)
                        .attachEvent('onWeixinJSBridgeReady', this.onWeixinJSBridgeReady)
                        .bind(this);
                }
            } else {
                this.onWeixinJSBridgeReady();
            }
            // 支付宝内置浏览器打开页面后调整字体大小
            if (typeof AlipayJSBridge === 'undefined') {
                if (document.addEventListener) {
                    document.addEventListener(
                        'AlipayJSBridgeReady',
                        this.onAlipayJSBridgeReady.bind(this),
                        false);
                } else if ((document as any).attachEvent) {
                    (document as any)
                        .attachEvent('AlipayJSBridgeReady', this.onAlipayJSBridgeReady)
                        .bind(this);
                }
            } else {
                this.onAlipayJSBridgeReady();
            }
        }
    }

    public onWeixinJSBridgeReady() {
        WeixinJSBridge.on('menu:setfont', () => {
            console.log('wx on menu:setfont');
            this.calc();
        });
    }

    /**
     * AlipayJSBridge 没有设置字体的时间回调
     * @HACK 在用户点击右上角打开菜单时启动循环调用计算字体大小
     */
    public onAlipayJSBridgeReady() {
        document.addEventListener('optionMenu', (e: any) => {
            console.log('alipay on optionMenu, start calc loop', e);
            this.recalc();
        });
    }

    /** 创建参照dom */
    public createReferDom(): HTMLDivElement {
        const div = document.createElement('div');

        div.style.cssText = 'width:1rem;height:0;overflow:hidden;position:absolute;z-index:-2;visibility:hidden;';
        document.body.append(div);

        return div;
    }

    /** 获取html.fontSize */
    public getOriginalHtmlFontSize(): number {
        const rootDom = document.querySelector('html');
        const fontSize = rootDom?.style.fontSize || 16;
        return this.toNumber(fontSize);
    }

    /** 计算html.fontSize */
    public calc() {
        if (!this.html) {
            return;
        }
        const clientWidth = this.html.clientWidth;
        if (clientWidth) {
            this.html.style.fontSize =  this.scale * 100
                * (clientWidth / this.designWidth)
                + 'px';
        } else {
            this.html.style.fontSize = '12px';
        }
    }

    public recalc() {
        if (this.reScaleTimer) {
            clearTimeout(this.reScaleTimer);
        }
        this.reScaleTimer = setTimeout(() => {
            this.toScale();
            this.calc();
            this.recalc();
        }, 1000);
    }

    /** 带单位的px 转换为数字 */
    public toNumber(val: string | number): number {
        if (typeof val === 'string') {
            val = +val.replace('px', '');
        }
        return val;
    }

    /** 计算变换系数 */
    public toScale() {
        const htmlFontSize: number = this.getOriginalHtmlFontSize();
        let referWidth: number = this.referDiv.offsetWidth;
        let scale = 1;

        if (window.getComputedStyle) {
            referWidth = this.toNumber(window.getComputedStyle(this.referDiv).width);
        }

        if (htmlFontSize && referWidth) {
            if (Math.abs(htmlFontSize - referWidth) < this.offset) {
                scale = 1;
            } else {
                scale = htmlFontSize / referWidth;
            }
        }
        this.scale = scale;

        return scale;
    }
}
